import {
  AnalyticsEventIdMessage,
  MessageFromParent,
  MessageFromParentType,
} from '../Providers/ParentMessageBusProvider/MessageFromParent';
import {
  MessageToParent,
  MessageToParentType,
} from '../Providers/ParentMessageBusProvider/MessageToParent';
import {devLog} from './dev';
import {readMessageFromParent} from './readMessageFromParent';

export type ParentMessageListenerFn = (message: MessageFromParent) => void;

export interface ParentMessageBus {
  // Send a message to the parent
  post: (message: MessageToParent) => void;

  // Request the latest analytics event ID from the parent
  requestAnalyticsEventId: () => Promise<string | null>;

  // Add and remove persistent message listeners
  addListener: (listener: ParentMessageListenerFn) => void;
  removeListener: (listener: ParentMessageListenerFn) => void;
}

export function createParentMessageBus({
  audience,
  entryPoint,
  parentOrigin,
}: {
  audience?: string;
  entryPoint?: string;
  parentOrigin: string;
}): ParentMessageBus {
  const listeners: ParentMessageListenerFn[] = [];

  function addListener(listener: ParentMessageListenerFn) {
    listeners.push(listener);
  }

  function removeListener(listener: ParentMessageListenerFn) {
    const index = listeners.findIndex((l) => l === listener);
    if (index >= 0) {
      listeners.splice(index, 1);
    }
  }

  function post(message: MessageToParent) {
    if (window.parent && window.parent !== window) {
      if (parentOrigin) {
        const wrapped = {
          ...message,
          audience,
          entryPoint,
        };
        devLog('Posting to parent', {message: wrapped, parentOrigin});
        window.parent.postMessage(wrapped, parentOrigin);
      } else {
        devLog(
          'parentOrigin is not set; if you are not running in Storybook this might be a problem',
          {
            message,
            parentOrigin,
          },
        );
      }
    } else {
      devLog('No parent window; are you not iframed?', {
        message,
        parentOrigin,
      });
    }
  }

  function request(
    message: MessageToParent,
    expectedResponseType: MessageFromParentType,
  ) {
    const requestTimeoutInMilliseconds = 500;

    return new Promise<MessageFromParent>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        removeListener(requestHandler);
        reject(new Error('Timed out waiting for a response'));
      }, requestTimeoutInMilliseconds);

      function requestHandler(fromParent: MessageFromParent) {
        if (fromParent.type === expectedResponseType) {
          clearTimeout(timeoutId);
          removeListener(requestHandler);
          resolve(fromParent);
        }
      }

      addListener(requestHandler);
      post(message);
    });
  }

  async function requestAnalyticsEventId(): Promise<string | null> {
    try {
      const result = await request(
        {
          type: MessageToParentType.RequestAnalyticsEventId,
        },
        MessageFromParentType.AnalyticsEventId,
      );
      return (result as AnalyticsEventIdMessage).data.analyticsEventId;
    } catch {
      return null;
    }
  }

  function handler(e: MessageEvent) {
    if (e.origin !== parentOrigin) {
      return;
    }

    if (!!e.data.audience && !!audience && e.data.audience !== audience) {
      return;
    }

    if (
      !!e.data.entryPoint &&
      !!entryPoint &&
      e.data.entryPoint !== entryPoint
    ) {
      return;
    }

    const message = readMessageFromParent(e.data);
    if (message) {
      // use slice() to get a clone of the listeners so we're safe if any of the
      // listeners modifies the listener list
      listeners.slice().forEach((l) => l(message));
    }
  }
  window.addEventListener('message', handler);

  return {
    post,
    requestAnalyticsEventId,
    addListener,
    removeListener,
  };
}
